/** @import {MaturityOptionsStepProps} from './MaturityOptionsStepTypes' */
/** @import {Instruction} from "./MaturityOptionsTypes" */
/** @import {OtherAccount} from '././PartialRenewal/PartialRenewalForm/TransferSection/TransferSectionTypes' */
import React, { useContext, useEffect, useState } from "react";
import { formatDate, formatSortCode, formatTime } from "../../utils/helpers";
import { CurrentTermDetailsBox } from "./MaturityDetailsBoxes";
import Button from "../Form/Button/Button";
import { constructDateTime } from "./MaturityFlowHelperFunctions";
import { useNavigate } from "react-router";
import './maturityInstructionForm.scss';
import {customerDataContext} from "../../contexts/CustomerDataProvider";
import {productTypes} from "../../utils/productHelpers";
import {MaturityOptions} from "./MaturityOptions"
import BestRatesBlock from "./PromiseOfBestRate/BestRateBlock"
import {useUnifiedAnalytics} from "../../contexts/AnalyticsWrapper";

function MaturityOptionsStep(/** @type {MaturityOptionsStepProps} */ {
    maturingAccount,
    onStepComplete,
    featureEnabled, accountList
}) {
    const navigate = useNavigate();
    const analytics = useUnifiedAnalytics();

    const goBack = () => {
        analytics.trackAnalyticsClickEvent(
            "close without making changes",
            document.referrer,
            "bottom",
            maturingAccount?.product?.code || '',
            "maturity",
            {},
            'link click',
            'navigation|close without making changes|maturity',
            'goBack'
        );
        navigate(-1);
    }

    /**
     * Maps an instruction key to a user-friendly display text
     * @param {string} key - The instruction key to map
     * @returns {string} The mapped display text or the original key if no mapping exists
     */
    function mapInstructionKeyToDisplayText(key) {
        /** @type {Record<string, string>} */
        const mapping = {
            'rollover_all_funds': 'renew everything',
            'rollover_partial_payaway_interest': 'renew partial',
            'rollover_payaway_interest': 'renew original balance only',
            'payaway_all_funds': 'transfer everything'
        };

        return mapping[key] || key;
    }

    const goToMaturityOptions = (/** @type {Instruction | null} */ instruction) => {
        if (!instruction || !instruction.onComplete) return;

        instruction.onComplete()
        window.scrollTo(0, 0);
    }

    // const displayMaxBalanceWarning = exceedsMaxBalance(maturingAccount)

    const data = useContext(customerDataContext);
    const products = data?.data?.customer.products.filter(p => p.type === productTypes.FixedTerm) || [];
    const [instruction, setInstruction] = useState(/** @type {Instruction | null} */(null));

    const [currencyInputValue, setCurrencyInputValue] = useState(
        maturingAccount?.maturity?.partialRolloverAmount?.toFixed(2)?.toString() ||
        maturingAccount?.balance?.toFixed(2)?.toString() ||
        '0');

    const [selectedAccount, setSelectedAccount] = useState(
        accountList?.filter(account => account.id === maturingAccount?.maturity?.destinationAccountId)[0] ||
        undefined);

    const isContinueButtonDisabled = () => {
        const isInstructionValid = instruction && instruction.key;
        const currencyInputValueNum = parseFloat(currencyInputValue);
        const maturingAccountBalanceNum = parseFloat(maturingAccount?.balance?.toString() || '0');
        
        if (currencyInputValueNum > maturingAccountBalanceNum) {
            return true;
        }

        if (instruction === null) {
            return !isInstructionValid;
        }

        switch (instruction.key) {
            case "rollover_partial_payaway_interest":
                if (currencyInputValueNum === maturingAccountBalanceNum) {
                    return !isInstructionValid;
                }
                return !(currencyInputValueNum > 0 && currencyInputValueNum < maturingAccountBalanceNum && selectedAccount);

            default:
                return !isInstructionValid; 
        }
    };

    return (<>
        <h1>Your {maturingAccount == undefined ? null : maturingAccount.product.name} is maturing soon</h1>
        <CurrentTermDetailsBox maturingAccount={maturingAccount} />
        <div className={"maturity3__flex-row maturity3__flex-row--responsive"}>
            <BestRatesBlock maturingAccount={maturingAccount} products={products || []} />
        </div>
        {/*<div className={"maturity3__border--bottom"} />*/}
        <h2 className={"maturity3__span_content"}>Choose your maturity instruction</h2>

        <MaturityOptions
            maturingAccount={maturingAccount}
            products={products}
            setInstruction={setInstruction}
            onStepComplete={onStepComplete}
            featureEnabled={featureEnabled}
            accountList={accountList}
            currencyInputValue={currencyInputValue}
            setCurrencyInputValue={setCurrencyInputValue}
            selectedAccount={selectedAccount}
            setSelectedAccount={setSelectedAccount}
        />
        <p>You can change your instruction at any time
            before <b>{maturingAccount?.maturity ? formatTime(constructDateTime(new Date(), maturingAccount.maturity.maturityWindowCloseTime)) : ""}</b> on {maturingAccount?.maturity?.maturityWindowCloseDate ? formatDate(maturingAccount.maturity.maturityWindowCloseDate) : ""}.
            {!maturingAccount?.maturity?.maturityInstruction &&
                <> If we don’t hear from you, we'll automatically pay your
                    money into your linked account (<b>{maturingAccount?.linkedAccountNumber} | {formatSortCode(maturingAccount?.linkedAccountSortCode)}</b>).</>
            }
        </p>
        <div className={"maturity3__flex-row maturity3__flex-row--left-align"}>
            <Button size='med' customClassNames={["navigation-button__back"]} label={"Back"}
                onClick={() => goBack()} />
            <Button
                size='med'
                customClassNames={["navigation-button__continue"]}
                disabled={isContinueButtonDisabled()}
                label={"Continue"}
                onClick={() => goToMaturityOptions(instruction)} />

        </div>
        <p className={"maturity3__aer-info"}>
            *AER stands for the Annual Equivalent Rate and illustrates what the interest rate would be if interest
            was paid and compounded
            once a year. The AER is intended to be an indicative rate to help you compare the return on different
            savings products.</p>
    </>)
}

export default MaturityOptionsStep
